(ns frontend.routing
  (:require
   [bidi.bidi :as bidi]
   [cljs.reader :as reader]
   [clojure.set :as set]
   [common.features.plan :as plan]
   [common.modules :as modules]
   [common.roles :as roles]
   [common.util :as cu]
   [frontend.db :as db]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [frontend.routes :as routes]
   [frontend.state.current-session :as session]
   [frontend.updates.entity-scope :as entity-scope]
   [frontend.utils :as fu]
   [frontend.view.grid.tasks-grid.db :as tasks-db]
   [frontend.view.onboarding.terms-acceptance.events :as terms]
   [goog.string.format]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.dom-globals :as dg]))
;;
;; Utils:
;;
(def ^:private pipeline-scoped-view-id->canonical
  "bidi/path-for disambiguates only by handler tag, never by which params
  are supplied - given a tag, it returns whichever route (bare or
  pipeline-scoped) it encounters first, silently ignoring extra/missing
  params (see bidi.bidi/unmatch-pair). So the pipeline-scoped variant of
  each listing route needs its own tag purely so a push can target it
  unambiguously (routing/set-view! [:oppos-grid-with-pipeline :pipeline-id
  id]). The moment the URL is read back, normalize straight back to the
  canonical view-id here, so render-view and every other view-id-keyed
  lookup (permission/sync gating, :active-nav, ...) only ever sees the
  handful of tags they already know about."
  {:oppos-grid-with-pipeline                     :oppos-grid
   :fundraising-opportunities-grid-with-pipeline :fundraising-opportunities-grid
   :sales-opportunities-grid-with-pipeline       :sales-opportunities-grid
   :exit-opportunities-grid-with-pipeline        :exit-opportunities-grid})

(defn- path->view
  [path]
  (some-> (bidi/match-route routes/routes path)
          (set/rename-keys {:handler :view-id})
          (update :view-id #(get pipeline-scoped-view-id->canonical % %))))

(defn notify-route-change-listener
  [view-params]
  (let [path js/window.location.pathname
        view (or (path->view path)
                 (do (logging/warn "no matching view for path" path)
                     {:view-id :not-found}))]
    (swap! db/app-state assoc
           :view
           (if view-params
             (assoc view :view-params view-params)
             view))))

(def routing-atom (atom {}))

;;
;; Initialize routing:
;;

(defn init!
  []
  (set! (.-onpopstate js/window)
        (fn [_]
          (notify-route-change-listener nil)))
  (notify-route-change-listener nil)
  (swap! db/app-state assoc :switching-tenant? (boolean (ls/get :switching-tenant?))))

;;
;; Public API:
;;

(def path-for (partial bidi/path-for routes/routes))

(defonce interceptors-atom (atom {}))

(defn set-view!
  ([path-args]
   (set-view! path-args nil))
  ([path-args view-params]
   (js/console.log "Set view : " path-args " > " view-params)
   (let [interceptors @interceptors-atom
         prevent-page-change? (->> interceptors
                                   (some (fn [[_ interceptor-fn]]
                                           (interceptor-fn path-args view-params))))]
     (when-not prevent-page-change?
       (if-let [path (apply path-for path-args)]
         (do
           #_(js/console.log "Set view : " path-args " >> " path)
           (js/window.history.pushState nil (pr-str path) path)
           (notify-route-change-listener view-params)
           ;; Scrolls to the top of the page
           (aset (.. js/document -documentElement) "scrollTop" 0))
         (logging/error "set-view!: unknown path:" (pr-str path-args)))))))

(defn add-change-interceptor
  [intercept-key interceptor-fn]
  (swap! interceptors-atom assoc intercept-key interceptor-fn))

(defn remove-change-interceptor
  [interceptor-key]
  (swap! interceptors-atom dissoc interceptor-key))

(defn view
  []
  @db/view)

(defn routed-view-id
  []
  @session/routed-view-id)

;;
;; Is view-id for view that can be rendered without login?
;;

(def no-login-required?
  #{:login
    :mobile
    :reports-printout
    :test
    :test-ribbon})

(defn login-required?
  [view]
  (not (some #{view} no-login-required?)))
;;
;; View does not require tenant specific data to be loaded?
;;
(def data-sync-not-needed?
  #{:login
    :mobile})

;;
;; Any view-ids except these should not be allowed for guests
;;

(def guests-allowed?
  #{:guest-home
    :company-guest
    :guest-kpi-report
    :guest-kpi-reports})

;;
;; View-ids listed here should only be accessible to admin users
;;

(def admin-required?
  #{:settings-company
    :settings-users
    :settings-tags-and-titles
    :settings-stages
    :settings-pass-reasons
    :settings-kpi-reports
    :new-settings/export-data
    :settings/deal-flow-pipelines
    :settings/fundraising-pipelines
    :settings.permissions/fields
    :settings.permissions/pipeline
    :new-settings
    :new-settings/guests
    :new-settings-web-form
    :new-settings/security
    :new-settings/authorized-applications
    :new-settings/account-information
    :settings/module-management
    :settings/mcp
    :settings/zap-vaults
    :new-settings/data-import
    :import-data
    :new-settings/api-integrations
    :settings/funds
    :settings/users
    :settings/guest-accounts
    :settings/teams})

;;
;; Render view multimethod:
;;
;; Note: multimethods don't work properly with reagent
;; form-2 and form-3 components
;; => always keep the method as a form-1 component
;;

(defmulti render-view :view-id)

(defn ^:export js2cljs
  [e encoded-params]
  (let [parsed (reader/read-string (.decodeURIComponent js/window encoded-params))]
    (dg/prevent-default e)
    (set-view! (first parsed) (second parsed))
    false))

(defn js->cljs-route
  [path-args view-params]
  (let [cb 'frontend.routing/js2cljs
        params (->> [path-args view-params]
                    (str)
                    (.encodeURIComponent js/window))]
    (fu/format "%s.%s(event,'%s')"
               (namespace cb)
               (name cb)
               params)))

(defn resolve-preferred-landing-page
  [{:keys [pipeline-key pipeline-id view-id route-params selected-task-tab] :as preferred-landing-page}]
  (let [{:keys [id]} route-params
        has-pipeline? (session/entity-in-use? pipeline-id)
        tenant-has-entity? (session/entity-in-use? id)
        can-access-selected-task-tab? (or (= selected-task-tab :task.type/simple)
                                          (session/dealflow?)
                                          (session/sales-pipelines?)
                                          (session/fundraising?))
        only-view-id? (and view-id
                           (= (set (keys preferred-landing-page)) #{:view-id}))]
    (cond
      (and view-id pipeline-id pipeline-key has-pipeline?)
      (do
        (swap! session/selected-pipelines assoc
               pipeline-key
               pipeline-id)
        {:view-id view-id})
      (and view-id selected-task-tab can-access-selected-task-tab?)
      (do
        (swap! tasks-db/tasks assoc :tasks-view selected-task-tab)
        {:view-id view-id})
      (and view-id route-params tenant-has-entity?)
      {:view-id      view-id
       :route-params route-params}
      only-view-id?
      {:view-id view-id}
      :else nil)))

(defn resolve-default-page-view
  []
  (let [account-preferences @session/account-preferences
        preferred-landing-page (get account-preferences :preferred-landing-page)
        resolved-preference (resolve-preferred-landing-page preferred-landing-page)]
    (cond
      resolved-preference
      resolved-preference
      (and (plan/available :feature/dealflow @session/tenant @session/role)
           (some #{:module/dealflow}
                 (modules/all-modules @session/tenant @session/tenant-plan))
           (seq @session/all-oppo-pipelines))
      {:view-id :oppos-grid}
      (and (plan/available :feature/fundraising @session/tenant @session/role)
           (some #{:module/fundraising}
                 (modules/all-modules @session/tenant @session/tenant-plan))
           (seq @session/all-froppo-pipelines))
      {:view-id :fundraising-opportunities-grid}
      (and (plan/available :feature/sales-pipelines @session/tenant @session/role)
           (some #{:module/sales-pipelines}
                 (modules/all-modules @session/tenant @session/tenant-plan))
           (seq @session/all-sales-oppo-pipelines))
      {:view-id :sales-opportunities-grid}
      (and (plan/available :feature/exit-process-management @session/tenant @session/role)
           (some #{:module/exit-process-management}
                 (modules/all-modules @session/tenant @session/tenant-plan))
           (seq @session/all-exit-oppo-pipelines))
      {:view-id :exit-opportunities-grid}
      :else
      {:view-id :personal-profile})))

(defn route-view
  [account-state account-role view]
  (let [view (if (= :default-page (:view-id view))
               (resolve-default-page-view)
               view)
        view-id (:view-id view)
        current-module @(r/track session/current-module)
        route-view
        (cond
          (no-login-required? view-id)
          view

          (= account-state :pending)
          {:view-id :loading}
          @db/switching-tenant?
          {:view-id :loading}

          (and (= account-state :role)                      ;; if logged in
               (not (data-sync-not-needed? view-id))        ;; not public page
               (not @(r/track session/data-synced?)))       ;; and sync has not finished
          {:view-id :syncing-data}                          ;; wait for data to be synced

          ;; if a guest is trying to access non-allowed views, he is redirected to guest-home
          (and (= account-state :role)
               (roles/is-guest-role? account-role)
               (not (guests-allowed? view-id)))
          (if (= account-role :role/guest-sell-side)
            (cond
              (= @session/target-entity-type :entity.type/workspace)
              {:view-id :workspace}
              (= @session/target-entity-type :entity.type/company)
              (let [target-id @session/target-id
                    reporting-on?
                    (and (plan/available :feature/kpi-reporting @session/tenant @session/role)
                         (some #{:module/kpi-reporting}
                               (modules/all-modules @session/tenant @session/tenant-plan)))
                    reports? (when reporting-on?
                               (->> @session/all-kpi-reports
                                    (cu/only-entity :kpi-report/company target-id)
                                    (remove :common/disabled?)
                                    (filter :kpi-report/kpi-cycle)
                                    (seq)))]
                (if reports?
                  {:view-id :guest-kpi-reports}
                  {:view-id :company-guest})))
            (cond (= @session/target-entity-type :entity.type/oppo)
                  {:view-id :deal-guest}
                  :else
                  {:view-id :not-found}))                   ;; TODO change to guest-home later when more things are added

          (and @session/account
               (not (:account/account-initialized? @session/account))
               (terms/has-to-accept-terms?))
          {:view-id :initialize-account}

          (terms/has-to-accept-terms?)
          {:view-id :accept-terms}

          (and (= account-state :role)
               (admin-required? view-id)
               (not (isa? roles/role-hierarchy account-role :role/admin)))
          {:view-id :not-found}                             ;;TODO access denied page

          ;; "/"-route forwards to module main page
          (and (= account-state :role)
               current-module
               (= :oppos-grid view-id)
               (not= current-module :module/dealflow))
          {:view-id (modules/module->route current-module)}

          (and (session/is-mobile?)
               (= account-state :role)                      ;; logged in
               (not current-module))                        ;; no available modules (fundraising not available for mobile atm)
          {:view-id :not-found}

          (= account-state :role)                           ;; logged in
          (if (and (= :oppos-grid view-id)
                   (or (not (some #{:module/dealflow} @session/account-modules))
                       (and (not (session/is-admin?))
                            (->> @session/all-oppo-pipelines
                                 (empty?))))
                   (some #{@session/role} [:role/user]))
            {:view-id :companies-grid}
            view)

          (nil? view)
          {:view-id :not-found}                             ;; unknown view

          :else
          {:view-id :login})]
    (when (not= (:view-id route-view) @session/routed-view-id)
      (swap! db/session assoc :view-id (:view-id route-view)))
    route-view))

(defn page-requires-login?
  [view]
  (let [view-id (:view-id view)
        login-required? (not (no-login-required? view-id))]
    login-required?))

(def clickable-entity-types
  #{:entity.type/company
    :entity.type/fof-asset
    :entity.type/fund
    :entity.type/fund-commitment
    :entity.type/fundraising-opportunity
    :entity.type/holding
    :entity.type/investor
    :entity.type/kyc-profile
    :entity.type/tenant-kyc-profile
    :entity.type/contract-kyc-profile
    :entity.type/oppo
    :entity.type/other-asset
    :entity.type/person
    :entity.type/real-estate
    :entity.type/sale-agreement
    :entity.type/sales-opportunity
    :entity.type/exit-opportunity
    :entity.type/company-counterparty-diligence})

()
(defn open-entity
  "Route to an entity's own page. `entity-or-id` may be an entity map or an id;
   the **type** is what routing needs, and it is resolved either from the map
   itself or from `@session/entities`.

   Pass the map whenever you have it. Under the new grid most entity types are
   no longer bulk-synced to the client, so an id-only call for one of those
   resolves no type at all - and a freshly created entity is never in that
   cache. `open-entity-type` is the explicit alternative when only an id is to
   hand."
  [entity-or-id & other-params]
  (let [entity-id (cond (:db/id entity-or-id)
                        (:db/id entity-or-id)
                        (number? entity-or-id)
                        entity-or-id
                        :else
                        (logging/warn "Routing/open-entity cannot resolve entity id from "
                                      entity-or-id))
        entity (get @session/entities entity-id entity-or-id)
        entity-type (:entity/type entity)
        default-routing-params (case entity-type
                                 :entity.type/zap-vault [:tab :documents]
                                 nil)]
    ;; Fail soft rather than routing to a nil handler. `set-view!` throws
    ;; "Cannot form URI from a nil handler" on a nil type, and callers run this
    ;; mid-callback - the creator's :on-ok! navigates, then closes its own
    ;; modal - so the throw took the close with it and left the modal up over a
    ;; dead overlay. Not navigating is a visible no-op; throwing here breaks
    ;; whatever called us.
    (cond
      entity-type
      (set-view! (concat [entity-type :id entity-id]
                         default-routing-params
                         other-params))

      ;; The session does not have the entity for this id. On the new grid this is
      ;; normal for a business record. Fetch the entity, then get its type once.
      ;; An old-grid account fetches nothing (`hydrate!` only calls back). The
      ;; warning below then appears, as before.
      (number? entity-id)
      (entity-scope/hydrate!
       {:entity-id entity-id
        :on-done   (fn []
                     (if-let [fetched-type (:entity/type (get @session/entities entity-id))]
                       (set-view! (concat [fetched-type :id entity-id]
                                          (case fetched-type
                                            :entity.type/zap-vault [:tab :documents]
                                            nil)
                                          other-params))
                       (logging/warn (str "Routing/open-entity: no :entity/type for "
                                          entity-id " - not navigating. Pass the entity "
                                          "map, or use open-entity-type when only the "
                                          "id is available."))))})

      :else
      (logging/warn (str "Routing/open-entity: no :entity/type for " entity-id
                         " - not navigating. Pass the entity map, or use "
                         "open-entity-type when only the id is available.")))))

(defn open-entity-type
  [entity-type entity-or-id & other-params]
  (js/console.log "open entity : " entity-or-id " > " other-params)
  (let [entity-id (cond (:db/id entity-or-id)
                        (:db/id entity-or-id)
                        (number? entity-or-id)
                        entity-or-id
                        :else
                        (logging/warn "Routing/open-entity cannot resolve entity id from "
                                      entity-or-id))]
    (set-view! (concat [entity-type
                        :id entity-id]
                       other-params))))

(rf/reg-fx
 ::old-routing
 (fn [{:keys [path-args view-params]}]
   (js/console.log "Old routing : " path-args " > " view-params)
   (set-view! path-args view-params)))
